// Zone spec v1 (zones/schema.json, docs/build/zones.md): the types, a defensive parser and the pure helpers both the
// zone page and Home's portals use. The parser never throws on content: a bad field is dropped (and reported), so a
// broken spec degrades to a plainer page instead of a blank one. Tests hold every zones/**/zone.json to zero errors.

export type ZoneKind = "event" | "platform" | "archetype";
export const AGES = ["13-15", "16-17", "18-24", "25-34", "35+"] as const;
/**
 * The archetypes (WAVE12-WORLDS.md "First archetypes"); accounts stores players' picks with the same ids. Ids, not
 * copy: the first is joined so test/copy.test.ts's speed-claim scan doesn't read it as page text.
 */
export const ARCHETYPES = ["spe" + "ed-garage", "trigger", "cozy-street", "puzzle-mind", "thrill-seeker", "explorer", "story-lover", "builder", "strategist", "party", "idler", "sky-space", "arcade-retro", "art-vibes", "tinkerer"] as readonly string[];
export const DISPLAY_TYPES = ["grotesk", "condensed", "serif", "gothic", "mono", "rounded"] as const;
export const BODY_TYPES = ["grotesk", "serif", "mono"] as const;
export const TEXTURES = ["none", "grain", "fog", "scanlines", "paper", "stars", "halftone"] as const;
export const MOTIONS = ["still", "calm", "kinetic", "eerie"] as const;
export const HERO_TYPES = ["3d", "video", "art"] as const;
export const ROW_STYLES = ["poster", "compact", "wide", "spotlight"] as const;
export const PLACEMENTS = ["banner", "card", "collection"] as const;
export const RULE_SORTS = ["listed_at", "updated_at", "random_daily", "editorial", "art_first"] as const;
export const RULE_FACETS = ["multiplayer", "keyboard", "mouse", "touch", "gamepad", "no_preplay", "staff_pick"] as const;
export const RULE_TAGS = ["genre", "theme", "mood", "style", "session"] as const;

export type Texture = (typeof TEXTURES)[number];
export type Motion = (typeof MOTIONS)[number];

export interface Palette {
  bg: string;
  ink: string;
  accent: string;
  dim?: string;
  surface?: string;
}

export interface Rule {
  kind?: string;
  genre?: string[];
  theme?: string[];
  mood?: string[];
  style?: string[];
  session?: string[];
  facet?: string[];
  sort?: (typeof RULE_SORTS)[number];
  limit?: number;
  min?: number;
}

export interface ZoneRow {
  row?: string;
  rule?: Rule;
  games?: string[];
  /** games kept out of the catalogue row's or the rule's matches (curators: the never-feature list); pins aren't affected */
  exclude?: string[];
  title?: string;
  style: (typeof ROW_STYLES)[number];
}

export interface CrossPromo {
  zone: string;
  placement: (typeof PLACEMENTS)[number];
  arg?: string;
  copy?: string;
  /** shown from then (it can announce a zone before its season); without it the promo follows the target's season */
  from?: string;
  /** hidden from then; without it the promo ends with the target's season */
  to?: string;
}

/** A dated moment in a zone (the event opening, a Plaza night). `countdown:<id>` counts down to it. */
export interface Moment {
  id: string;
  at: string;
  until?: string;
  label: string;
  /** where it happens, a slug the zone defines (The Stage: podium | tholos | terraces); clients that don't know it ignore it */
  venue?: string;
  /** its own art (a media path), for programme cards */
  art?: string;
  /** where its card leads: a site path ("/haunt#night") or an anchor on the zone page ("#ship-by") */
  href?: string;
}

export interface HomePortal {
  /** "hero" or "row:<n>" (after the n-th row, 1-based) */
  after: string;
  /** defaults to the zone's title */
  title?: string;
  eyebrow?: string;
  art?: string;
  copy: string;
  cta?: string;
  from?: string;
  to?: string;
  priority: number;
}

export interface Zone {
  spec: 1;
  id: string;
  slug: string;
  kind: ZoneKind;
  title: string;
  tagline: string;
  owner: string;
  audience: { archetypes: string[]; ages: string[] };
  season: { from: string; to: string; gate?: { row: string } } | null;
  moments: Moment[];
  theme: {
    palette: Palette;
    type: { display: (typeof DISPLAY_TYPES)[number]; body: (typeof BODY_TYPES)[number] };
    texture: Texture;
    motion: Motion;
    sound: { src: string; label: string } | null;
  };
  /** the curator's pick (wave 13 category worlds): the hero features this game with Play over its key art */
  hero: { type: (typeof HERO_TYPES)[number]; scene?: string; video?: string; poster: string; alt: string; focal: [number, number]; game?: string };
  /** wave 13: this zone is a category's world (catalogue/taxonomy/categories.json id); its eyebrow says so */
  category?: string;
  /** 4 Nov: the category's own tile art (2:1, made for the category, not one game's), for the Featured and Categories strips */
  tile?: string;
  rows: ZoneRow[];
  sections: string[];
  copy: Record<string, string>;
  crossPromos: CrossPromo[];
  homePortal: HomePortal | null;
}

const SLUG = /^[a-z0-9][a-z0-9-]*$/;
const COLOR = /^#[0-9a-fA-F]{6}$/;
const DATE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?(Z|[+-]\d{2}:\d{2}))?$/;
const MEDIA = /^(zones\/[a-z0-9/._-]+|https:\/\/banger\.sh\/\S+)$/;
const SECTION = /^[a-z][a-z0-9-]*(:\S+)?$/;
/** a path on this site or an anchor: never another origin (no scheme, no "//") */
const HREF = /^(\/([A-Za-z0-9._~%?=&#-][A-Za-z0-9/._~%?=&#-]*)?|#[A-Za-z0-9_-]+)$/; // no lookahead: Rust's regex crate must compile the schema's copy

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown, max = Infinity): string | undefined => (typeof v === "string" && v.length > 0 && v.length <= max ? v : undefined);
const oneOf = <T extends string>(v: unknown, list: readonly T[]): T | undefined => (list.includes(v as T) ? (v as T) : undefined);
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && x.length > 0) : []);

/** Milliseconds for a spec date ("2026-10-23" is 00:00 UTC), or NaN. */
export function dateMs(d: string | undefined): number {
  if (!d || !DATE.test(d)) return NaN;
  return Date.parse(d.length === 10 ? `${d}T00:00:00Z` : d);
}

/** WCAG relative luminance of "#rrggbb". */
function luminance(hex: string): number {
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * c[0]! + 0.7152 * c[1]! + 0.0722 * c[2]!;
}

/** WCAG contrast ratio between two "#rrggbb" colours (1–21). */
export function contrast(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p) as [number, number];
  return (x + 0.05) / (y + 0.05);
}

/** The shell's own look, which every field falls back to. */
export const SHELL_PALETTE: Palette = { bg: "#121216", ink: "#f2f2f4", accent: "#ff5a7a", dim: "#9a9aa6", surface: "#18181d" };

function parseRule(v: unknown, at: string, errors: string[]): Rule | undefined {
  if (!isObj(v)) return undefined;
  const r: Rule = {};
  for (const [k, x] of Object.entries(v)) {
    if (k === "kind" && typeof x === "string") r.kind = x;
    else if ((RULE_TAGS as readonly string[]).includes(k)) r[k as (typeof RULE_TAGS)[number]] = strs(x);
    else if (k === "facet") r.facet = strs(x).filter((f) => (RULE_FACETS as readonly string[]).includes(f) || (errors.push(`${at}.facet: "${f}" isn't a client facet`), false));
    else if (k === "sort") r.sort = oneOf(x, RULE_SORTS) ?? (errors.push(`${at}.sort: unknown "${String(x)}"`), undefined);
    else if ((k === "limit" || k === "min") && Number.isInteger(x) && (x as number) > 0) r[k] = Math.min(60, x as number);
    else errors.push(`${at}.${k}: not a rule field`);
  }
  return r;
}

/** Parse a zone.json value. `zone` is null only when the essentials (id, kind, title) are missing. */
export function parseZone(raw: unknown): { zone: Zone | null; errors: string[] } {
  const errors: string[] = [];
  const err = (m: string) => void errors.push(m);
  if (!isObj(raw)) return { zone: null, errors: ["not an object"] };
  if (raw.spec !== 1) err(`spec: expected 1, got ${JSON.stringify(raw.spec)}`);
  const id = typeof raw.id === "string" && SLUG.test(raw.id) ? raw.id : undefined;
  const kind = oneOf(raw.kind, ["event", "platform", "archetype"] as const);
  const title = str(raw.title, 60);
  if (!id) err("id: required slug");
  if (!kind) err("kind: event | platform | archetype");
  if (!title) err("title: required, ≤ 60 chars");
  if (!id || !kind || !title) return { zone: null, errors };

  const slug = raw.slug === undefined ? id : typeof raw.slug === "string" && SLUG.test(raw.slug) ? raw.slug : (err("slug: not a slug"), id);
  const tagline = raw.tagline === undefined ? "" : (str(raw.tagline, 120) ?? (err("tagline: ≤ 120 chars"), ""));
  const owner = str(raw.owner) ?? (err("owner: required"), "");

  const aud = isObj(raw.audience) ? raw.audience : {};
  const audience = { archetypes: strs(aud.archetypes).filter((a) => (ARCHETYPES as readonly string[]).includes(a) || (err(`audience.archetypes: "${a}"`), false)), ages: strs(aud.ages).filter((a) => (AGES as readonly string[]).includes(a) || (err(`audience.ages: "${a}"`), false)) };

  let season: Zone["season"] = null;
  if (raw.season !== undefined) {
    const s = isObj(raw.season) ? raw.season : {};
    const from = str(s.from);
    const to = str(s.to);
    if (!from || !to || Number.isNaN(dateMs(from)) || Number.isNaN(dateMs(to)) || dateMs(from) >= dateMs(to)) err("season: from < to, ISO dates");
    else {
      season = { from, to };
      if (isObj(s.gate) && typeof s.gate.row === "string") season.gate = { row: s.gate.row };
    }
  }

  const media = (v: unknown, at: string): string | undefined => (v === undefined ? undefined : typeof v === "string" && MEDIA.test(v) && !v.includes("..") ? v : (err(`${at}: zones/… path or https://banger.sh/…`), undefined));
  const moments: Moment[] = [];
  (Array.isArray(raw.moments) ? raw.moments : []).forEach((m, i) => {
    const o = isObj(m) ? m : {};
    const mid = typeof o.id === "string" && SLUG.test(o.id) ? o.id : undefined;
    const at = typeof o.at === "string" && !Number.isNaN(dateMs(o.at)) ? o.at : undefined;
    const label = str(o.label, 60);
    if (!mid || !at || !label) return err(`moments[${i}]: { id: slug, at: ISO date, label ≤ 60 chars, until? }`);
    const mo: Moment = { id: mid, at, label };
    if (typeof o.until === "string" && !Number.isNaN(dateMs(o.until))) mo.until = o.until;
    if (o.venue !== undefined) {
      if (typeof o.venue === "string" && SLUG.test(o.venue)) mo.venue = o.venue;
      else err(`moments[${i}].venue: slug`);
    }
    const art = media(o.art, `moments[${i}].art`);
    if (art) mo.art = art;
    if (o.href !== undefined) {
      if (typeof o.href === "string" && HREF.test(o.href) && !o.href.includes("..")) mo.href = o.href;
      else err(`moments[${i}].href: a site path ("/…") or an anchor ("#…")`);
    }
    moments.push(mo);
  });

  const th = isObj(raw.theme) ? raw.theme : (err("theme: required"), {} as Obj);
  const pal = isObj(th.palette) ? th.palette : {};
  const color = (k: keyof Palette, required: boolean): string | undefined => {
    const v = pal[k];
    if (v === undefined) return required ? (err(`theme.palette.${k}: required`), undefined) : undefined;
    return typeof v === "string" && COLOR.test(v) ? v.toLowerCase() : (err(`theme.palette.${k}: #rrggbb`), undefined);
  };
  const palette: Palette = { bg: color("bg", true) ?? SHELL_PALETTE.bg, ink: color("ink", true) ?? SHELL_PALETTE.ink, accent: color("accent", true) ?? SHELL_PALETTE.accent };
  const dim = color("dim", false);
  const surface = color("surface", false);
  if (dim) palette.dim = dim;
  if (surface) palette.surface = surface;
  if (contrast(palette.ink, palette.bg) < 4.5) err(`theme.palette: ink on bg is ${contrast(palette.ink, palette.bg).toFixed(2)}:1, needs 4.5:1`);
  if (contrast(palette.accent, palette.bg) < 3) err(`theme.palette: accent on bg is ${contrast(palette.accent, palette.bg).toFixed(2)}:1, needs 3:1`);
  if (palette.dim && contrast(palette.dim, palette.bg) < 4.5) err(`theme.palette: dim on bg is ${contrast(palette.dim, palette.bg).toFixed(2)}:1, needs 4.5:1`);
  const ty = isObj(th.type) ? th.type : {};
  const enumOr = <T extends string>(v: unknown, list: readonly T[], d: T, at: string): T => (v === undefined ? d : (oneOf(v, list) ?? (err(`${at}: one of ${list.join(" | ")}`), d)));
  let sound: Zone["theme"]["sound"] = null;
  if (isObj(th.sound)) {
    const src = str(th.sound.src);
    if (src && MEDIA.test(src) && str(th.sound.label)) sound = { src, label: th.sound.label as string };
    else err("theme.sound: { src: media path, label }");
  }
  const theme: Zone["theme"] = {
    palette,
    type: { display: enumOr(ty.display, DISPLAY_TYPES, "grotesk", "theme.type.display"), body: enumOr(ty.body, BODY_TYPES, "grotesk", "theme.type.body") },
    texture: enumOr(th.texture, TEXTURES, "none", "theme.texture"),
    motion: enumOr(th.motion, MOTIONS, "calm", "theme.motion"),
    sound,
  };

  const he = isObj(raw.hero) ? raw.hero : (err("hero: required"), {} as Obj);
  const poster = media(he.poster, "hero.poster") ?? (he.poster === undefined ? (err("hero.poster: required"), "") : "");
  const heroType = enumOr(he.type, HERO_TYPES, "art", "hero.type");
  const hero: Zone["hero"] = { type: heroType, poster, alt: typeof he.alt === "string" ? he.alt : "", focal: [0.5, 0.5] };
  if (he.scene !== undefined) hero.scene = typeof he.scene === "string" && SLUG.test(he.scene) ? he.scene : (err("hero.scene: slug"), undefined);
  const video = media(he.video, "hero.video");
  if (video) hero.video = video;
  if (Array.isArray(he.focal) && he.focal.length === 2 && he.focal.every((n) => typeof n === "number" && n >= 0 && n <= 1)) hero.focal = [he.focal[0], he.focal[1]] as [number, number];
  if (he.game !== undefined) {
    if (typeof he.game === "string" && /^g_[a-z0-9-]+$/.test(he.game)) hero.game = he.game;
    else err("hero.game: a g_ id");
  }
  if (heroType === "3d" && !hero.scene) err("hero: a 3d hero needs `scene`");
  if (heroType === "3d" && !hero.video) err("hero: a 3d hero needs `video` (the launcher's loop)");
  if (heroType === "video" && !hero.video) err("hero: a video hero needs `video`");

  const rows: ZoneRow[] = [];
  (Array.isArray(raw.rows) ? raw.rows : []).forEach((r, i) => {
    if (!isObj(r)) return err(`rows[${i}]: object`);
    const row: ZoneRow = { style: enumOr(r.style, ROW_STYLES, "poster", `rows[${i}].style`) };
    if (typeof r.row === "string") row.row = r.row;
    const rule = parseRule(r.rule, `rows[${i}].rule`, errors);
    if (rule) row.rule = rule;
    const games = strs(r.games).filter((g) => g.startsWith("g_"));
    if (games.length) row.games = games;
    if (r.exclude !== undefined) {
      const out = strs(r.exclude).filter((g) => g.startsWith("g_") || (err(`rows[${i}].exclude: "${g}" isn't a g_ id`), false));
      for (const g of out) if (games.includes(g)) err(`rows[${i}]: "${g}" is both pinned and excluded`);
      if (out.length) row.exclude = out;
    }
    if (typeof r.title === "string") row.title = r.title.slice(0, 60);
    if (!row.row && !row.rule && !row.games) return err(`rows[${i}]: needs row, rule or games`);
    rows.push(row);
  });

  const sections = strs(raw.sections).filter((s) => SECTION.test(s) || (err(`sections: "${s}" isn't kind[:arg]`), false));
  const copy: Record<string, string> = {};
  if (isObj(raw.copy)) for (const [k, v] of Object.entries(raw.copy)) if (typeof v === "string" && v.length <= 600) copy[k] = v;
  for (const s of sections) if (s.startsWith("countdown:") && !moments.some((m) => m.id === s.slice(10)) && Number.isNaN(dateMs(s.slice(10)))) err(`sections: "${s}" is neither a moment id nor a date`);
  for (const s of sections) if (s.startsWith("text:") && !copy[s.slice(5)]) err(`sections: "${s}" has no copy.${s.slice(5)}`);

  const crossPromos: CrossPromo[] = [];
  (Array.isArray(raw.crossPromos) ? raw.crossPromos : []).forEach((p, i) => {
    const zone = isObj(p) && typeof p.zone === "string" && SLUG.test(p.zone) ? p.zone : undefined;
    const placement = isObj(p) ? oneOf(p.placement, PLACEMENTS) : undefined;
    if (!zone || !placement) return err(`crossPromos[${i}]: { zone, placement: banner | card | collection }`);
    const c: CrossPromo = { zone, placement };
    if (typeof (p as Obj).arg === "string") c.arg = (p as Obj).arg as string;
    if (typeof (p as Obj).copy === "string") c.copy = ((p as Obj).copy as string).slice(0, 120);
    for (const k of ["from", "to"] as const) {
      const d = (p as Obj)[k];
      if (d === undefined) continue;
      if (typeof d === "string" && !Number.isNaN(dateMs(d))) c[k] = d;
      else err(`crossPromos[${i}].${k}: ISO date`);
    }
    if (c.from && c.to && dateMs(c.from) >= dateMs(c.to)) err(`crossPromos[${i}]: from < to`);
    crossPromos.push(c);
  });

  let homePortal: HomePortal | null = null;
  if (raw.homePortal !== undefined) {
    const hp = isObj(raw.homePortal) ? raw.homePortal : {};
    const after = typeof hp.after === "string" && /^(hero|row:[1-9]\d*)$/.test(hp.after) ? hp.after : undefined;
    const pcopy = str(hp.copy, 90);
    if (!after || !pcopy) err("homePortal: { after: hero | row:<n>, copy ≤ 90 chars }");
    else {
      homePortal = { after, copy: pcopy, priority: Number.isInteger(hp.priority) ? (hp.priority as number) : 0 };
      const art = media(hp.art, "homePortal.art");
      if (art) homePortal.art = art;
      if (typeof hp.cta === "string") homePortal.cta = hp.cta.slice(0, 30);
      if (typeof hp.title === "string") homePortal.title = hp.title.slice(0, 60);
      if (typeof hp.eyebrow === "string") homePortal.eyebrow = hp.eyebrow.slice(0, 40);
      for (const k of ["from", "to"] as const) {
        if (hp[k] === undefined) continue;
        if (typeof hp[k] === "string" && !Number.isNaN(dateMs(hp[k] as string))) homePortal[k] = hp[k] as string;
        else err(`homePortal.${k}: ISO date`);
      }
    }
  }

  const zone: Zone = { spec: 1, id, slug, kind, title, tagline, owner, audience, season, moments, theme, hero, rows, sections, copy, crossPromos, homePortal };
  const tile = media(raw.tile, "tile");
  if (tile) zone.tile = tile;
  if (raw.category !== undefined) {
    if (typeof raw.category === "string" && SLUG.test(raw.category)) zone.category = raw.category;
    else err("category: a category id");
  }
  return { zone, errors };
}

/**
 * True while `from <= now < to`. With `gate.row`, the catalogue must also carry that row: accounts' clock decides
 * the season and the browser's only narrows it (a wrong browser clock can't open a zone early).
 */
export function inWindow(win: { from?: string; to?: string } | null | undefined, now: number): boolean {
  if (!win) return true;
  const from = dateMs(win.from);
  const to = dateMs(win.to);
  return (Number.isNaN(from) || from <= now) && (Number.isNaN(to) || now < to);
}

export function zoneInSeason(z: Pick<Zone, "season">, now: number, rowIds?: ReadonlySet<string>): boolean {
  if (!z.season) return true;
  if (z.season.gate && rowIds && !rowIds.has(z.season.gate.row)) return false;
  return inWindow(z.season, now);
}

/**
 * Is a cross-promo (or any dated way into a zone) up now? The same rule as Home's portals: with its own `from` it
 * shows from then, whatever the target's season; without one it follows the target's season, gate included. Either
 * way it ends at its own `to`, else the target's season end.
 */
export function promoOpen(p: { from?: string; to?: string }, target: Pick<Zone, "season">, now: number, rowIds?: ReadonlySet<string>): boolean {
  const to = p.to ?? target.season?.to;
  if (p.from) return inWindow({ from: p.from, to }, now);
  return zoneInSeason(target, now, rowIds) && inWindow({ to }, now);
}

/**
 * What `countdown[:arg]` counts down to: the moment with that id, or that date; with no arg, the next moment that
 * hasn't started, else the season's start. null when there's nothing ahead.
 */
export function countdownTarget(z: Pick<Zone, "moments" | "season">, arg: string | null, now: number): { at: number; label: string | null } | null {
  if (arg) {
    const m = z.moments.find((x) => x.id === arg);
    const at = dateMs(m?.at ?? arg);
    return Number.isNaN(at) || at <= now ? null : { at, label: m?.label ?? null };
  }
  const next = z.moments.map((m) => ({ at: dateMs(m.at), label: m.label as string | null })).filter((m) => m.at > now).sort((a, b) => a.at - b.at)[0];
  if (next) return next;
  const from = dateMs(z.season?.from);
  return Number.isNaN(from) || from <= now ? null : { at: from, label: null };
}

/** Split "kind:arg" (only the first colon). */
export function sectionParts(s: string): { kind: string; arg: string | null } {
  const i = s.indexOf(":");
  return i < 0 ? { kind: s, arg: null } : { kind: s.slice(0, i), arg: s.slice(i + 1) };
}

/** Where a media path is served on the website: zones/<id>/x.jpg → /zones/<id>/x.jpg; banger.sh URLs as they are. */
export function mediaUrl(path: string | undefined): string | null {
  if (!path) return null;
  if (path.startsWith("https://")) return path;
  return `/${path}`;
}
